import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { timelogsApi, CreateTimeLogRequest, TimeLogFilters, TimeLogSummaryFilters } from '@/api'
import { taskKeys } from './useTasks'

/**
 * Query keys for timelog-related queries
 */
export const timelogKeys = {
  all: ['timelogs'] as const,
  lists: () => [...timelogKeys.all, 'list'] as const,
  list: (filters?: TimeLogFilters) => [...timelogKeys.lists(), filters] as const,
  summaries: () => [...timelogKeys.all, 'summary'] as const,
  summary: (filters?: TimeLogSummaryFilters) => [...timelogKeys.summaries(), filters] as const,
}

/**
 * Hook to fetch paginated list of time logs
 */
export function useTimeLogs(filters?: TimeLogFilters) {
  return useQuery({
    queryKey: timelogKeys.list(filters),
    queryFn: () => timelogsApi.list(filters),
  })
}

/**
 * Hook to fetch time log summary with breakdowns
 */
export function useTimeLogSummary(filters?: TimeLogSummaryFilters) {
  return useQuery({
    queryKey: timelogKeys.summary(filters),
    queryFn: () => timelogsApi.summary(filters),
  })
}

/**
 * Hook to get today's time summary
 */
export function useTodayTimeLogSummary() {
  const today = new Date().toISOString().split('T')[0]
  return useTimeLogSummary({
    from_date: today,
    to_date: today,
  })
}

/**
 * Hook to get this week's time summary
 */
export function useWeekTimeLogSummary() {
  const today = new Date()
  const monday = new Date(today)
  monday.setDate(today.getDate() - today.getDay() + 1) // Monday

  return useTimeLogSummary({
    from_date: monday.toISOString().split('T')[0],
    to_date: today.toISOString().split('T')[0],
  })
}

/**
 * Hook to create a new time log entry
 */
export function useCreateTimeLog() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTimeLogRequest) => timelogsApi.create(data),
    onSuccess: (createdLog) => {
      // Invalidate all timelog queries
      queryClient.invalidateQueries({ queryKey: timelogKeys.all })
      // Also invalidate the related task to update time_logged_minutes
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(createdLog.task_id) })
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() })
    },
  })
}
